"use client";

import { getCompanyName } from "@/lib/textron-data-processor";
import {
  AlertCircle,
  ArrowLeft,
  CheckCircle,
  MessageSquare,
} from "lucide-react";
import React, {
  startTransition,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { DocumentManager } from "../../planning/DocumentManager";
import { ReviewComment } from "../../shared/ReviewComment";
import { Button } from "../../ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../../ui/card";
import { Label } from "../../ui/label";
import { RadioGroup, RadioGroupItem } from "../../ui/radio-group";
import { getFY_YEAR } from "@/utils/sharepoint-env";

interface InvsmtSubstantiveProps {
  onBack?: () => void;
  setActiveSection?: (section: string) => void;
  procedureId?: string; // Optional: if provided, show only this procedure's content
}

type QuestionId =
  | "inv-12"
  | "inv-13"
  | "inv-14"
  | "inv-15"
  | "INVSMT-INFERENCE";

const QUESTION_IDS: QuestionId[] = [
  "inv-12",
  "inv-13",
  "inv-14",
  "inv-15",
  "INVSMT-INFERENCE",
];

interface QuestionState {
  selectedOption?: string;
  textResponse?: { html: string; plainText: string };
  documents?: Array<{
    id: string;
    type: string;
    name: string;
    content?: string;
    url?: string;
  }>;
  table?: any;
}

export default function InvsmtSubstantive({
  onBack,
  setActiveSection,
  procedureId,
}: InvsmtSubstantiveProps) {
  // If no onBack is provided but setActiveSection is, create a back handler
  const handleBack =
    onBack ||
    (() => {
      if (setActiveSection) {
        setActiveSection("invsmt-substantive");
      }
    });
  const [companyName, setCompanyName] = useState<string>("");
  const [areaPrefix, setAreaPrefix] = useState<string>("INVST"); // Default prefix
  const [questionStates, setQuestionStates] = useState<
    Record<QuestionId, QuestionState>
  >({
    "inv-12": { documents: [] },
    "inv-13": { documents: [] },
    "inv-14": { documents: [] },
    "inv-15": { documents: [] },
    "INVSMT-INFERENCE": { documents: [] },
  });
  const [dirtyQuestions, setDirtyQuestions] = useState<Set<QuestionId>>(
    () => new Set(),
  );
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [hasExistingFile, setHasExistingFile] = useState(false);
  const [isEditMode, setIsEditMode] = useState<boolean>(false);
  const [reviewCommentQuestionId, setReviewCommentQuestionId] = useState<
    string | null
  >(null);

  // Generate question ID for a section
  const getSectionQuestionId = (sectionName: string): string => {
    const normalizedSection = sectionName
      .replace(/[^a-zA-Z0-9\s]/g, "")
      .replace(/\s+/g, "_")
      .toUpperCase();
    return `INVESTMENTS_${normalizedSection}`;
  };

  const markDirty = (id: QuestionId) => {
    setDirtyQuestions((prev) => {
      const next = new Set(prev);
      next.add(id);
      return next;
    });
  };

  const isQuestionAnswered = (id: QuestionId): boolean => {
    const state = questionStates[id];
    if (!state) return false;
    return !!(
      state.selectedOption ||
      (state.textResponse &&
        ((state.textResponse.html && state.textResponse.html.trim()) ||
          (state.textResponse.plainText && state.textResponse.plainText.trim()))) ||
      (state.documents && state.documents.length > 0) ||
      (state.table && Array.isArray(state.table) && state.table.length > 0)
    );
  };

  // Load company name first
  useEffect(() => {
    const loadCompanyName = async () => {
      try {
        const name = await getCompanyName("combined");
        setCompanyName(name || "ABC Private Limited");

      } catch (error) {
        console.warn(
          "[InvsmtSubstantive] Failed to load company name, using fallback",
        );
        setCompanyName("ABC Private Limited");
      }
    };
    loadCompanyName();
  }, []);

  // Load area prefix from Sections.json
  useEffect(() => {
    const loadAreaPrefix = async () => {
      try {
        if ((window as any).sharePointAPI?.downloadSectionsJson) {
          const result = await (
            window as any
          ).sharePointAPI.downloadSectionsJson();
          if (result?.success && result?.data) {
            const sectionsData = result.data;
            // Look for "Investments" in Execution section
            if (
              sectionsData.Execution &&
              sectionsData.Execution["Investments"]
            ) {
              setAreaPrefix(sectionsData.Execution["Investments"]);
            }
          }
        }
      } catch (error) {
        console.warn(
          "[InvsmtSubstantive] Failed to load area prefix from Sections.json, using default INVST:",
          error,
        );
      }
    };
    loadAreaPrefix();
  }, []);

  // Load Execution_<PREFIX>_Responses.json
  useEffect(() => {
    if (!companyName || !areaPrefix) return;

    const loadResponses = async () => {
      try {
        const fileName = `Execution_${areaPrefix}_Responses.json`;
        const result = await (
          window as any
        ).sharePointAPI?.downloadClientJson?.(fileName);

        if (
          result?.success &&
          result.data &&
          result.data.substantiveProcedures
        ) {
          const data = result.data.substantiveProcedures as Record<string, any>;

          const nextStates: Record<QuestionId, QuestionState> = {
            "inv-12": { documents: [] },
            "inv-13": { documents: [] },
            "inv-14": { documents: [] },
            "inv-15": { documents: [] },
            "INVSMT-INFERENCE": { documents: [] },
          };

          QUESTION_IDS.forEach((qid) => {
            const q = data[qid];
            if (!q) return;

            const docs = Array.isArray(q.documents)
              ? q.documents.map((d: any) => {
                  if (typeof d === "string") {
                    return { id: `doc_${d}`, type: "linked", name: d };
                  }
                  return {
                    id: d.id || `doc_${d.name || d.fileName}`,
                    type: d.type || "linked",
                    name: d.name || d.fileName || "",
                    content: d.content,
                    url: d.url,
                  };
                })
              : [];

            nextStates[qid] = {
              selectedOption: q.selectedOption || "",
              textResponse: q.textResponse || { html: "", plainText: "" },
              documents: docs,
              table: q.table || [],
            };
          });

          setQuestionStates(nextStates);
          setHasExistingFile(true);
        } else {
          setHasExistingFile(false);
        }
      } catch (error) {
        console.error(
          `[InvsmtSubstantive] Failed to load Execution_${areaPrefix}_Responses.json from SharePoint:`,
          error,
        );
        setHasExistingFile(false);
      }
    };

    loadResponses();
  }, [companyName, areaPrefix]);

  const handleOptionChange = (questionId: QuestionId, value: string) => {
    setQuestionStates((prev) => ({
      ...prev,
      [questionId]: {
        ...prev[questionId],
        selectedOption: value,
      },
    }));
    markDirty(questionId);
  };

  const handleDocumentsChange = (questionId: QuestionId, documents: any[]) => {
    setQuestionStates((prev) => ({
      ...prev,
      [questionId]: {
        ...prev[questionId],
        documents: documents,
      },
    }));
    markDirty(questionId);
  };

  const handleTextResponseChange = (
    questionId: QuestionId,
    textResponse: { html: string; plainText: string },
  ) => {
    setQuestionStates((prev) => ({
      ...prev,
      [questionId]: {
        ...prev[questionId],
        textResponse: textResponse,
      },
    }));
    markDirty(questionId);
  };

  const handleSave = useCallback(async () => {
    if (!companyName || !areaPrefix) {
      console.warn(
        "[InvsmtSubstantive] Cannot save: company name not available",
      );
      return;
    }

    // Check if there's any content to save (even if not marked dirty)
    const questionsWithContent = new Set<QuestionId>();
    QUESTION_IDS.forEach((qid) => {
      const state = questionStates[qid] || {};
      const hasTextbox = localStorage.getItem(`textbox_${qid}`);
      const hasDocuments = state.documents && state.documents.length > 0;
      const hasTextResponse =
        state.textResponse &&
        (state.textResponse.html || state.textResponse.plainText);
      const hasOption = state.selectedOption;
      if (hasTextbox || hasDocuments || hasTextResponse || hasOption) {
        questionsWithContent.add(qid);
      }
    });

    if (dirtyQuestions.size === 0 && questionsWithContent.size === 0) {
      setIsEditMode(false);
      return;
    }

    // Use dirty questions OR questions with content (for first-time saves)
    const questionsToSave = new Set([
      ...dirtyQuestions,
      ...questionsWithContent,
    ]);

    // Auto-save any pending textbox content before processing responses
    const documentManagers = document.querySelectorAll(
      "[data-document-manager]",
    );
    for (const manager of Array.from(documentManagers)) {
      const itemId = manager.getAttribute("data-item-id");
      if (itemId) {
        const autoSaveMethod = (window as any)[`autoSaveTextbox_${itemId}`];
        if (autoSaveMethod && typeof autoSaveMethod === "function") {
          autoSaveMethod();
        }
      }
    }

    // Wait for auto-save to complete
    await new Promise((resolve) => setTimeout(resolve, 1500));

    setIsSaving(true);
    setSaveError(null);

    try {
      const fileName = `Execution_${areaPrefix}_Responses.json`;

      // Load existing file first so we can merge per question_id
      let existingData: any = {};
      try {
        const result = await (
          window as any
        ).sharePointAPI?.downloadClientJson?.(fileName);
        if (result?.success && result.data) {
          existingData = result.data;
        }
      } catch (error) {
        console.warn(
          `[InvsmtSubstantive] Failed to download existing ${fileName}, starting from empty`,
          error,
        );
      }

      if (
        !existingData ||
        typeof existingData !== "object" ||
        Array.isArray(existingData)
      ) {
        existingData = {};
      }

      if (!existingData.substantiveProcedures) {
        existingData.substantiveProcedures = {};
      }

      // Build payload using questions to save
      const payload: any = {
        substantiveProcedures: {} as Record<string, any>,
      };

      for (const qid of questionsToSave) {
        const state = questionStates[qid] || { documents: [] };

        // Check localStorage for textbox content (highest priority - ensures latest content)
        const textboxFromLocalStorage = localStorage.getItem(`textbox_${qid}`);
        const hasTextboxContent =
          textboxFromLocalStorage && textboxFromLocalStorage.trim().length > 0;

        // For inference question, query db.json for section field like CustomProcedure
        let documentNames: string[] = [];
        if (qid === "INVSMT-INFERENCE") {
          const sectionToFind = `Execution_${areaPrefix}_${qid}`;
          try {
            if ((window as any).sharePointAPI?.loadDocumentListBySection) {
              const dbResult = await (
                window as any
              ).sharePointAPI.loadDocumentListBySection(sectionToFind);
              if (
                dbResult?.success &&
                dbResult?.data &&
                Array.isArray(dbResult.data)
              ) {
                documentNames = dbResult.data
                  .map((file: any) => file.name || "")
                  .filter((name: string) => name && name.trim().length > 0);
              }
            }
          } catch (error) {
            console.warn(
              `Failed to query db.json for section ${sectionToFind}:`,
              error,
            );
          }
        } else {
          // Extract documents (excluding textbox type)
          const fileDocuments = (state.documents || []).filter(
            (d: any) => d.type !== "textbox",
          );
          documentNames = fileDocuments.map((d: any) => d.name || d);
        }

        // Build textResponse - localStorage takes priority, then state
        let textResponse = { html: "", plainText: "" };
        if (hasTextboxContent) {
          textResponse = {
            html: textboxFromLocalStorage,
            plainText: textboxFromLocalStorage.replace(/<[^>]*>/g, ""), // Strip HTML tags
          };
        } else if (state.textResponse) {
          textResponse = state.textResponse;
        }

        payload.substantiveProcedures[qid] = {
          selectedOption: state.selectedOption || "",
          textResponse: textResponse,
          documents: documentNames,
          table: state.table || [],
          lastUpdated: new Date().toISOString(),
          companyName: companyName || "",
        };
      }

      // Merge only modified question_ids into existingData.substantiveProcedures
      Object.entries(payload.substantiveProcedures).forEach(([qid, value]) => {
        existingData.substantiveProcedures[qid] = value;
      });

      const jsonString = JSON.stringify(existingData, null, 2);
      const base64Content = btoa(unescape(encodeURIComponent(jsonString)));

      const uploadResult = await (window as any).sharePointAPI?.uploadFile?.({
        fileContent: base64Content,
        fileName: fileName,
        referenceValue: `Execution ${areaPrefix} Substantive Responses`,
        folderName: "juggernaut",
        fyYear: getFY_YEAR(),
        sectionList: [`Execution_${areaPrefix}_Responses`],
      });

      if (!uploadResult || !uploadResult.success) {
        console.error(
          `[InvsmtSubstantive] Failed to upload ${fileName}:`,
          uploadResult?.error,
        );
        throw new Error(uploadResult?.error || `Failed to save ${fileName}`);
      }

      setHasExistingFile(true);
      // Clear dirty flags on successful save (only clear questions that were actually saved)
      setDirtyQuestions(new Set());
      setIsEditMode(false);
    } catch (error: any) {
      console.error(
        `[InvsmtSubstantive] Error while saving ${fileName}:`,
        error,
      );
      setSaveError(
        error?.message ||
          "Failed to save Investments Substantive responses to SharePoint",
      );
    } finally {
      setIsSaving(false);
    }
  }, [companyName, areaPrefix, questionStates]);

  const saveButtonLabel = useMemo(() => {
    if (isSaving) return "Saving...";
    if (saveError) return "Save (Retry)";
    if (hasExistingFile) return "Save Changes";
    return "Save";
  }, [hasExistingFile, isSaving, saveError]);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-white">
            Investments - Substantive Procedures
          </h2>
          <p className="text-gray-400">Answer the following.</p>
        </div>
        {(onBack || setActiveSection) && (
          <Button variant="outline" onClick={handleBack}>
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back
          </Button>
        )}
      </div>

      <Card className="border-white/10 bg-black/30 text-white">
        <CardHeader className="sticky top-0 z-10 bg-black/30 backdrop-blur">
          <div className="flex items-center justify-between">
            <CardTitle className="text-white">
              Investments Substantive Procedures
            </CardTitle>
            <div className="flex items-center gap-2">
              {!isEditMode ? (
                <Button
                  onClick={() => {
                    startTransition(() => {
                      setIsEditMode(true);
                    });
                  }}
                  variant="outline"
                  size="sm"
                  className="border-white/20 text-white hover:bg-white/10"
                >
                  Edit
                </Button>
              ) : (
                <div className="flex items-center gap-2">
                  <Button
                    onClick={handleSave}
                    disabled={isSaving}
                    size="sm"
                    className="bg-green-600 text-white hover:bg-green-700"
                  >
                    {saveButtonLabel}
                  </Button>
                  <Button
                    onClick={() => setIsEditMode(false)}
                    variant="outline"
                    size="sm"
                    className="border-white/20 text-white hover:bg-white/10"
                  >
                    Stop Editing
                  </Button>
                </div>
              )}
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Question 1: inv-12 */}
          <Card className="border-white/10 bg-white/5 text-white">
            <CardContent className="space-y-3 p-4">
              <div className="flex items-start gap-3">
                <div className="flex-shrink-0">
                  {isQuestionAnswered("inv-12") ? (
                    <CheckCircle className="h-5 w-5 text-green-400" />
                  ) : (
                    <AlertCircle className="h-5 w-5 text-white/40" />
                  )}
                </div>
                <div className="flex-1 space-y-2">
                  <div className="flex items-start justify-between gap-2">
                    <div className="text-sm font-medium text-white">
                      <span className="font-semibold">1. </span>
                      Document our procedures performed to verify the respective
                      account balance, class of transaction or disclosure.
                    </div>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() =>
                        setReviewCommentQuestionId(
                          getSectionQuestionId(
                            "Investments Substantive Procedures - Q1",
                          ),
                        )
                      }
                      className="h-7 w-7 flex-shrink-0 p-1 text-yellow-400 hover:bg-white/10 hover:text-yellow-300"
                      title="Review Comment"
                    >
                      <MessageSquare className="h-4 w-4" />
                    </Button>
                  </div>
                  <DocumentManager
                    sectionKey="invst-substantive"
                    itemId="inv-12"
                    itemQuestion="Document our procedures performed to verify the respective account balance, class of transaction or disclosure."
                    itemTextResponse={questionStates["inv-12"].textResponse}
                    itemDocuments={questionStates["inv-12"].documents}
                    onDocumentsChange={(documents) =>
                      handleDocumentsChange("inv-12", documents)
                    }
                    onTextResponseChange={(textResponse) =>
                      handleTextResponseChange("inv-12", textResponse)
                    }
                    disabled={!isEditMode}
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Question 2: inv-13 */}
          <Card className="border-white/10 bg-white/5 text-white">
            <CardContent className="space-y-3 p-4">
              <div className="flex items-start gap-3">
                <div className="flex-shrink-0">
                  {isQuestionAnswered("inv-13") ? (
                    <CheckCircle className="h-5 w-5 text-green-400" />
                  ) : (
                    <AlertCircle className="h-5 w-5 text-white/40" />
                  )}
                </div>
                <div className="flex-1 space-y-3">
                  <div className="flex items-start justify-between gap-2">
                    <div className="text-sm font-medium text-white">
                      <span className="font-semibold">2. </span>
                      Has the management used any expert in arriving at the
                      estimate?
                    </div>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() =>
                        setReviewCommentQuestionId(
                          getSectionQuestionId(
                            "Investments Substantive Procedures - Q2",
                          ),
                        )
                      }
                      className="h-7 w-7 flex-shrink-0 p-1 text-yellow-400 hover:bg-white/10 hover:text-yellow-300"
                      title="Review Comment"
                    >
                      <MessageSquare className="h-4 w-4" />
                    </Button>
                  </div>
                  <RadioGroup
                    value={questionStates["inv-13"].selectedOption || ""}
                    onValueChange={(value) =>
                      handleOptionChange("inv-13", value)
                    }
                    disabled={!isEditMode}
                    className="ml-8"
                  >
                    {["Yes", "No"].map((opt) => (
                      <div key={opt} className="flex items-center space-x-2">
                        <RadioGroupItem
                          value={opt}
                          id={`inv-13-${opt}`}
                          className="border-white/20 text-[#4da3ff] focus:ring-[#4da3ff]/20"
                        />
                        <Label
                          htmlFor={`inv-13-${opt}`}
                          className="cursor-pointer text-sm text-white/80"
                        >
                          {opt}
                        </Label>
                      </div>
                    ))}
                  </RadioGroup>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Question 3: inv-14 - Only shown if question 2 is "Yes" */}
          {questionStates["inv-13"].selectedOption === "Yes" && (
            <Card className="border-white/10 bg-white/5 text-white">
              <CardContent className="space-y-3 p-4">
                <div className="flex items-start gap-3">
                  <div className="flex-shrink-0">
                    {isQuestionAnswered("inv-14") ? (
                      <CheckCircle className="h-5 w-5 text-green-400" />
                    ) : (
                      <AlertCircle className="h-5 w-5 text-white/40" />
                    )}
                  </div>
                  <div className="flex-1 space-y-3">
                    <div className="flex items-start justify-between gap-2">
                      <div className="text-sm font-medium text-white">
                        <span className="font-semibold">3. </span>
                        Have we used the work of management's expert as audit
                        evidence?
                      </div>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() =>
                          setReviewCommentQuestionId(
                            getSectionQuestionId(
                              "Investments Substantive Procedures - Q3",
                            ),
                          )
                        }
                        className="h-7 w-7 flex-shrink-0 p-1 text-yellow-400 hover:bg-white/10 hover:text-yellow-300"
                        title="Review Comment"
                      >
                        <MessageSquare className="h-4 w-4" />
                      </Button>
                    </div>
                    <RadioGroup
                      value={questionStates["inv-14"].selectedOption || ""}
                      onValueChange={(value) =>
                        handleOptionChange("inv-14", value)
                      }
                      disabled={!isEditMode}
                      className="ml-8"
                    >
                      {["Yes", "No"].map((opt) => (
                        <div key={opt} className="flex items-center space-x-2">
                          <RadioGroupItem
                            value={opt}
                            id={`inv-14-${opt}`}
                            className="border-white/20 text-[#4da3ff] focus:ring-[#4da3ff]/20"
                          />
                          <Label
                            htmlFor={`inv-14-${opt}`}
                            className="cursor-pointer text-sm text-white/80"
                          >
                            {opt}
                          </Label>
                        </div>
                      ))}
                    </RadioGroup>
                    {questionStates["inv-14"].selectedOption === "Yes" && (
                      <div className="mt-2">
                        <DocumentManager
                          sectionKey="invst-substantive"
                          itemId="inv-14"
                          itemQuestion="Have we used the work of management's expert as audit evidence?"
                          itemTextResponse={
                            questionStates["inv-14"].textResponse
                          }
                          itemDocuments={questionStates["inv-14"].documents}
                          onDocumentsChange={(documents) =>
                            handleDocumentsChange("inv-14", documents)
                          }
                          onTextResponseChange={(textResponse) =>
                            handleTextResponseChange("inv-14", textResponse)
                          }
                          disabled={!isEditMode}
                        />
                      </div>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Question 4: inv-15 */}
          <Card className="border-white/10 bg-white/5 text-white">
            <CardContent className="space-y-3 p-4">
              <div className="flex items-start gap-3">
                <div className="flex-shrink-0">
                  {isQuestionAnswered("inv-15") ? (
                    <CheckCircle className="h-5 w-5 text-green-400" />
                  ) : (
                    <AlertCircle className="h-5 w-5 text-white/40" />
                  )}
                </div>
                <div className="flex-1 space-y-3">
                  <div className="flex items-start justify-between gap-2">
                    <div className="text-sm font-medium text-white">
                      <span className="font-semibold">4. </span>
                      Have we used an expert to determine the recoverable
                      amount/value-in-use as per the applicable financial
                      reporting framework?
                    </div>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() =>
                        setReviewCommentQuestionId(
                          getSectionQuestionId(
                            "Investments Substantive Procedures - Q4",
                          ),
                        )
                      }
                      className="h-7 w-7 flex-shrink-0 p-1 text-yellow-400 hover:bg-white/10 hover:text-yellow-300"
                      title="Review Comment"
                    >
                      <MessageSquare className="h-4 w-4" />
                    </Button>
                  </div>
                  <RadioGroup
                    value={questionStates["inv-15"].selectedOption || ""}
                    onValueChange={(value) =>
                      handleOptionChange("inv-15", value)
                    }
                    disabled={!isEditMode}
                    className="ml-8"
                  >
                    {["Yes", "No"].map((opt) => (
                      <div key={opt} className="flex items-center space-x-2">
                        <RadioGroupItem
                          value={opt}
                          id={`inv-15-${opt}`}
                          className="border-white/20 text-[#4da3ff] focus:ring-[#4da3ff]/20"
                        />
                        <Label
                          htmlFor={`inv-15-${opt}`}
                          className="cursor-pointer text-sm text-white/80"
                        >
                          {opt}
                        </Label>
                      </div>
                    ))}
                  </RadioGroup>
                  {questionStates["inv-15"].selectedOption === "Yes" && (
                    <>
                      <div className="mt-1 text-xs text-white/60">
                        Document the procedures relating to use of auditor's
                        expert in Auditor's Expert workspace.
                      </div>
                      <div className="mt-2">
                        <DocumentManager
                          sectionKey="invst-substantive"
                          itemId="inv-15"
                          itemQuestion="Have we used an expert to determine the recoverable amount/value-in-use as per the applicable financial reporting framework?"
                          itemTextResponse={
                            questionStates["inv-15"].textResponse
                          }
                          itemDocuments={questionStates["inv-15"].documents}
                          onDocumentsChange={(documents) =>
                            handleDocumentsChange("inv-15", documents)
                          }
                          onTextResponseChange={(textResponse) =>
                            handleTextResponseChange("inv-15", textResponse)
                          }
                          disabled={!isEditMode}
                        />
                      </div>
                    </>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Inference and Conclusion Question */}
          <Card className="border-white/10 bg-white/5 text-white">
            <CardContent className="space-y-3 p-4">
              <div className="flex items-start gap-3">
                <div className="flex-shrink-0">
                  {isQuestionAnswered("INVSMT-INFERENCE") ? (
                    <CheckCircle className="h-5 w-5 text-green-400" />
                  ) : (
                    <AlertCircle className="h-5 w-5 text-white/40" />
                  )}
                </div>
                <div className="flex-1 space-y-3">
                  <div className="flex items-start justify-between gap-2">
                    <div className="text-sm font-medium text-white">
                      <span className="font-semibold">5. </span>
                      Document the inference and conclusion from the above
                      procedure performed.
                    </div>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() =>
                        setReviewCommentQuestionId(
                          getSectionQuestionId(
                            "Investments Substantive Procedures - Inference",
                          ),
                        )
                      }
                      className="h-7 w-7 flex-shrink-0 p-1 text-yellow-400 hover:bg-white/10 hover:text-yellow-300"
                      title="Review Comment"
                    >
                      <MessageSquare className="h-4 w-4" />
                    </Button>
                  </div>
                  <DocumentManager
                    sectionKey="invst-substantive"
                    itemId="INVSMT-INFERENCE"
                    itemQuestion="Document the inference and conclusion from the above procedure performed."
                    itemTextResponse={
                      questionStates["INVSMT-INFERENCE"].textResponse
                    }
                    itemDocuments={questionStates["INVSMT-INFERENCE"].documents}
                    onDocumentsChange={(documents) =>
                      handleDocumentsChange("INVSMT-INFERENCE", documents)
                    }
                    onTextResponseChange={(textResponse) =>
                      handleTextResponseChange("INVSMT-INFERENCE", textResponse)
                    }
                    disabled={!isEditMode}
                  />
                </div>
              </div>
            </CardContent>
          </Card>
        </CardContent>
      </Card>

      {/* Review Comment Modal */}
      {reviewCommentQuestionId && (
        <ReviewComment
          questionId={reviewCommentQuestionId}
          questionText={(() => {
            // Map questionId back to section name for display
            const sectionMap: Record<string, string> = {
              [getSectionQuestionId("Investments Substantive Procedures")]:
                "Investments Substantive Procedures",
            };
            return (
              sectionMap[reviewCommentQuestionId] || reviewCommentQuestionId
            );
          })()}
          isOpen={!!reviewCommentQuestionId}
          onClose={() => setReviewCommentQuestionId(null)}
        />
      )}
    </div>
  );
}
